import { InMemoryCache } from '@apollo/client';
import type { Cache, Reference } from '@apollo/client';
import { getOperationName } from '@apollo/client/utilities';

/**
 * RP-6194: timing span recorded by {@link InstrumentedInMemoryCache} for each `write` or `batch`
 * call. The STRESS harness drains these per case via the debug global's `drainSpans()` method and
 * renders them on the long-task timeline.
 */
export interface CacheTimingSpan {
  kind: 'write' | 'batch';
  graphqlOperationName: string | undefined;
  startMs: number;
  durationMs: number;
  watchCount: number;
  depth: number;
}

// Caps memory if the harness stops draining (it drains once per case); once full, the oldest
// spans are dropped.
const MAX_SPANS = 2048;

/**
 * Fixed-capacity ring buffer that evicts the oldest span when full. Zero allocations during
 * {@link push} — the backing array is pre-allocated.
 */
class SpanRingBuffer {
  private _buf: (CacheTimingSpan | undefined)[] = new Array(MAX_SPANS);
  private _writeIdx = 0;
  private _count = 0;

  push(span: CacheTimingSpan): void {
    this._buf[this._writeIdx] = span;
    this._writeIdx = (this._writeIdx + 1) % MAX_SPANS;
    if (this._count < MAX_SPANS) this._count++;
  }

  drain(): CacheTimingSpan[] {
    if (this._count === 0) return [];
    const start = (this._writeIdx - this._count + MAX_SPANS) % MAX_SPANS;
    const result: CacheTimingSpan[] = new Array(this._count);
    for (let i = 0; i < this._count; i++) {
      result[i] = this._buf[(start + i) % MAX_SPANS]!;
    }
    // Release drained spans without reallocating the backing array
    this._buf.fill(undefined);
    this._writeIdx = 0;
    this._count = 0;
    return result;
  }
}

/**
 * RP-6194: `InMemoryCache` subclass that records timing spans for `write` and `batch` calls and
 * tracks active watcher count. Used only on opted-in non-production pages — gated by
 * `isApolloCacheDebugGlobalEnabled` (see config/stressDebugHooks).
 */
export class InstrumentedInMemoryCache extends InMemoryCache {
  private _spans = new SpanRingBuffer();
  private _depth = 0;
  private _activeWatchCount = 0;
  private _watcherGraphqlOperationNames = new Map<() => void, string | undefined>();

  override write(options: Cache.WriteOptions): Reference | undefined {
    const graphqlOperationName = getOperationName(options.query) ?? undefined;
    const depth = this._depth;
    const watchCount = this._activeWatchCount;
    const startMs = performance.now();
    this._depth++;
    try {
      return super.write(options);
    } finally {
      this._depth--;
      this._spans.push({
        kind: 'write',
        graphqlOperationName,
        startMs,
        durationMs: performance.now() - startMs,
        watchCount,
        depth,
      });
    }
  }

  override batch<TUpdateResult>(
    options: Cache.BatchOptions<InMemoryCache, TUpdateResult>
  ): TUpdateResult {
    const depth = this._depth;
    const watchCount = this._activeWatchCount;
    const startMs = performance.now();
    this._depth++;
    try {
      return super.batch(options);
    } finally {
      this._depth--;
      this._spans.push({
        kind: 'batch',
        graphqlOperationName: undefined,
        startMs,
        durationMs: performance.now() - startMs,
        watchCount,
        depth,
      });
    }
  }

  override watch<TData = any, TVariables = any>(
    watchOptions: Cache.WatchOptions<TData, TVariables>
  ): () => void {
    this._activeWatchCount++;
    const graphqlOperationName = getOperationName(watchOptions.query) ?? undefined;
    const unsub = super.watch(watchOptions);
    let unsubscribed = false;
    const wrappedUnsub = () => {
      if (unsubscribed) return;
      unsubscribed = true;
      this._activeWatchCount--;
      this._watcherGraphqlOperationNames.delete(wrappedUnsub);
      unsub();
    };
    this._watcherGraphqlOperationNames.set(wrappedUnsub, graphqlOperationName);
    return wrappedUnsub;
  }

  drainSpans(): CacheTimingSpan[] {
    return this._spans.drain();
  }

  getActiveWatchCount(): number {
    return this._activeWatchCount;
  }

  getWatchersByGraphqlOperationName(): Record<string, number> {
    const counts: Record<string, number> = {};
    for (const opName of this._watcherGraphqlOperationNames.values()) {
      const key = opName ?? '(anonymous)';
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  }
}
